<template>
    <div id="page-watch" data-page="watch" class="watch-page-container py-4">
        <div class="container">
            <!-- Header Bar -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 watch-header-bar">
                <div class="d-flex align-items-center mb-2 mb-md-0">
                    <router-link to="/" class="d-flex align-items-center text-decoration-none mr-3" data-test="link-watch-brand">
                        <img :src="logoUrl" alt="VelocyTrack Logo" class="watch-brand-logo mr-2" />
                        <span class="font-weight-bold text-white h5 mb-0">VelocyTrack</span>
                    </router-link>
                    <span class="badge badge-info px-2 py-1 small font-weight-bold">PRODUCT WALKTHROUGH</span>
                </div>
                <div class="d-flex flex-wrap align-items-center watch-header-actions">
                    <router-link to="/demo" class="btn btn-sm btn-info font-weight-bold mr-2" data-test="btn-watch-launch-demo">
                        <i class="bi bi-terminal-fill mr-1"></i> Try Interactive Sandbox
                    </router-link>
                    <router-link to="/" class="btn btn-sm btn-outline-light font-weight-bold" data-test="btn-watch-back">
                        <i class="bi bi-arrow-left mr-1"></i> Back to Overview
                    </router-link>
                </div>
            </div>

            <!-- Main Watch Player Hero (Primary Content Above the Fold for Google Video Indexing) -->
            <section class="watch-player-section mb-4" aria-labelledby="watch-page-title">
                <div class="mb-3">
                    <h1 id="watch-page-title" class="watch-main-title font-weight-bold text-white mb-2">
                        VelocyTrack — Engineering Cockpit &amp; Time Intelligence Walkthrough
                    </h1>
                    <p class="watch-subtitle mb-0">
                        See how VelocyTrack connects Azure DevOps work items, sub-3-second sprint time logging, automated regional holiday capacity, and multi-repository pull request hygiene in one developer-first cockpit.
                    </p>
                </div>

                <div class="watch-video-frame rounded shadow-lg p-1 p-sm-2 mb-3">
                    <video
                        ref="watchVideoPlayer"
                        :poster="previewTimelog"
                        controls
                        playsinline
                        preload="metadata"
                        class="w-100 rounded d-block watch-video-element"
                        data-test="video-watch-player"
                        @error="onVideoError"
                        @playing="onVideoPlaying"
                        @loadedmetadata="onVideoLoadedMetadata"
                    >
                        <source :src="demoVideoUrl" type="video/mp4">
                        <track
                            label="English captions"
                            kind="subtitles"
                            srclang="en"
                            :src="demoVideoVttUrl"
                        >
                        Your browser does not support the video tag.
                    </video>
                </div>

                <div v-if="videoError" class="alert alert-warning mb-3 d-flex align-items-center justify-content-between" data-test="alert-watch-video-error">
                    <div>
                        <i class="bi bi-exclamation-triangle-fill mr-2"></i>
                        Could not stream video directly in browser.
                    </div>
                    <a :href="demoVideoUrl" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-outline-dark font-weight-bold ml-3">
                        Open Direct MP4 &nearr;
                    </a>
                </div>

                <!-- Action Strip Under Video -->
                <div class="d-flex flex-wrap justify-content-between align-items-center p-3 rounded watch-meta-bar">
                    <div class="small text-light-muted mb-2 mb-md-0">
                        <i class="bi bi-soundwave text-info mr-1"></i>
                        <strong>Duration:</strong> {{ demoVideoDurationLabel }} &bull; Narrated 9-chapter engineering walkthrough &bull;
                        <a :href="demoVideoUrl" target="_blank" rel="noopener noreferrer" class="text-info font-weight-bold ml-1 text-decoration-none" data-test="link-watch-direct-mp4">
                            Direct MP4 stream &nearr;
                        </a>
                    </div>
                    <div class="d-flex flex-wrap align-items-center watch-cta-group">
                        <router-link to="/demo" class="btn btn-info font-weight-bold mr-sm-2 mb-2 mb-sm-0" data-test="btn-watch-try-sandbox">
                            <i class="bi bi-play-circle-fill mr-1"></i> Launch Zero-Signup Demo
                        </router-link>
                        <router-link to="/#waitlist" class="btn btn-outline-info font-weight-bold" data-test="btn-watch-claim-beta">
                            <i class="bi bi-rocket-takeoff-fill mr-1"></i> Claim Free Beta Spot
                        </router-link>
                    </div>
                </div>
            </section>

            <!-- 4-Card Key Highlights Grid (Balanced 2x2 on Tablet, 4-col on Desktop, 1-col on Phone) -->
            <section class="mb-4" aria-label="Walkthrough Highlights">
                <h2 class="h5 font-weight-bold text-white mb-3">What You Will See in This Walkthrough</h2>
                <div class="watch-highlights-grid">
                    <div class="watch-highlight-card p-3 rounded" data-test="watch-card-timelog">
                        <div class="font-weight-bold text-info mb-1">
                            <i class="bi bi-clock-history mr-1"></i> 1. Fast (&lt;3s) Sprint Timelog Matrix
                        </div>
                        <p class="small text-light-muted mb-0">
                            Log daily engineering effort directly against sprint tasks and PBIs in one keyboard-friendly matrix without opening sluggish 6-click work item modals.
                        </p>
                    </div>
                    <div class="watch-highlight-card p-3 rounded" data-test="watch-card-capacity">
                        <div class="font-weight-bold text-info mb-1">
                            <i class="bi bi-calendar-check mr-1"></i> 2. Automated Holiday &amp; Sprint Capacity
                        </div>
                        <p class="small text-light-muted mb-0">
                            Regional public holiday calendars (100+ countries) and team PTO automatically deduct from sprint capacity gauges and pacing indicators.
                        </p>
                    </div>
                    <div class="watch-highlight-card p-3 rounded" data-test="watch-card-prs">
                        <div class="font-weight-bold text-info mb-1">
                            <i class="bi bi-git mr-1"></i> 3. Multi-Repo Pull Request Radar
                        </div>
                        <p class="small text-light-muted mb-0">
                            Track open pull requests, reviewer votes, inline comment threads, and stale branches with ahead/behind commit safety warnings across every repository.
                        </p>
                    </div>
                    <div class="watch-highlight-card p-3 rounded" data-test="watch-card-security">
                        <div class="font-weight-bold text-info mb-1">
                            <i class="bi bi-shield-lock-fill mr-1"></i> 4. Zero-Clone Security Architecture
                        </div>
                        <p class="small text-light-muted mb-0">
                            VelocyTrack never clones or stores your source code. All work item and repository metadata is accessed transiently via AES-256 encrypted tokens.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import mixinAccount from '../mixinAccount';
    import { getLogoUrl } from '../envHelper';

    const WATCH_VIDEO_LD_NODE = {
        '@type': 'VideoObject',
        '@id': 'https://beta.velocytrack.com/watch#demo-video',
        name: 'VelocyTrack — Product Demo Walkthrough',
        description: 'Narrated 9-chapter engineering walkthrough showing how VelocyTrack connects Azure DevOps work items, sprint time tracking, automated holiday capacity, and multi-repo pull requests in one cockpit.',
        thumbnailUrl: 'https://beta.velocytrack.com/og-image.png',
        uploadDate: '2026-10-15T00:00:00Z',
        duration: 'PT4M39S',
        contentUrl: 'https://beta.velocytrack.com/videos/velocytrack-demo.mp4'
    };

    export default {
        name: 'page-watch',
        mixins: [mixinAccount],
        data() {
            return {
                videoError: false,
                demoVideoDurationLabel: '4m 39s'
            };
        },
        computed: {
            logoUrl() {
                return getLogoUrl(this.currentEnvironment);
            },
            demoVideoUrl() {
                const version = process.env.VUE_APP_GIT_COMMIT || 'v2';
                return `${process.env.BASE_URL || '/'}videos/velocytrack-demo.mp4?v=${version}`;
            },
            demoVideoVttUrl() {
                const version = process.env.VUE_APP_GIT_COMMIT || 'v2';
                return `${process.env.BASE_URL || '/'}videos/velocytrack-demo.vtt?v=${version}`;
            },
            previewTimelog() {
                return `${process.env.BASE_URL || '/'}img/preview/preview-timelog.png`;
            }
        },
        mounted() {
            this.syncWatchVideoStructuredData(true);
            this.syncCaptionPreference();
        },
        beforeUnmount() {
            this.syncWatchVideoStructuredData(false);
        },
        methods: {
            syncWatchVideoStructuredData(includeVideo) {
                if (typeof document === 'undefined') return;
                try {
                    const ldEl = document.getElementById('vt-ld-json') || document.querySelector('script[type="application/ld+json"]');
                    if (!ldEl) return;
                    const data = JSON.parse(ldEl.textContent || '{}');
                    if (!Array.isArray(data['@graph'])) return;
                    const filtered = data['@graph'].filter(n => n && n['@type'] !== 'VideoObject');
                    if (includeVideo) {
                        filtered.push({ ...WATCH_VIDEO_LD_NODE });
                    }
                    data['@graph'] = filtered;
                    ldEl.textContent = JSON.stringify(data, null, 2);
                } catch (e) {
                    // Ignore JSON-LD sync errors
                }
            },
            onVideoPlaying() {
                this.videoError = false;
                this.syncCaptionPreference();
            },
            onVideoLoadedMetadata() {
                const video = this.$refs.watchVideoPlayer;
                if (video && video.duration && isFinite(video.duration)) {
                    const totalSec = Math.round(video.duration);
                    const mins = Math.floor(totalSec / 60);
                    const secs = totalSec % 60;
                    this.demoVideoDurationLabel = `${mins}m ${secs < 10 ? '0' : ''}${secs}s`;
                }
                this.syncCaptionPreference();
            },
            onVideoError() {
                this.videoError = true;
            },
            syncCaptionPreference() {
                const video = this.$refs.watchVideoPlayer;
                if (!video || !video.textTracks) return;
                let pref = null;
                try {
                    pref = localStorage.getItem('vt_demo_captions');
                } catch (e) {
                    // Ignore storage error
                }
                const shouldShow = pref === '1';
                for (let i = 0; i < video.textTracks.length; i++) {
                    video.textTracks[i].mode = shouldShow ? 'showing' : 'disabled';
                }
            }
        }
    };
</script>

<style scoped>
    .watch-page-container {
        min-height: 100vh;
        background: #090d16;
        color: #f8fafc;
        overflow-x: hidden;
    }

    .watch-header-bar {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .watch-brand-logo {
        width: 34px;
        height: 34px;
        border-radius: 6px;
    }

    .watch-main-title {
        font-size: clamp(1.4rem, 2.8vw, 2.15rem);
        line-height: 1.25;
        overflow-wrap: break-word;
        max-width: 100%;
    }

    .watch-subtitle {
        color: #94a3b8;
        font-size: clamp(0.92rem, 1.5vw, 1.05rem);
        max-width: 880px;
        line-height: 1.6;
    }

    .watch-video-frame {
        background: rgba(15, 23, 42, 0.85);
        border: 1px solid rgba(56, 189, 248, 0.3);
    }

    .watch-video-element {
        max-height: 74vh;
        background: #020617;
    }

    .watch-meta-bar {
        background: rgba(15, 23, 42, 0.75);
        border: 1px solid rgba(255, 255, 255, 0.08);
    }

    .text-light-muted {
        color: #94a3b8;
    }

    .watch-highlights-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
    }

    .watch-highlight-card {
        background: rgba(15, 23, 42, 0.75);
        border: 1px solid rgba(255, 255, 255, 0.08);
    }

    @media (max-width: 1024px) {
        .watch-highlights-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 820px) {
        .watch-cta-group {
            flex-direction: column;
            width: 100%;
        }

        .watch-cta-group .btn {
            width: 100%;
            margin-right: 0 !important;
        }
    }

    @media (max-width: 640px) {
        .watch-highlights-grid {
            grid-template-columns: 1fr;
        }
    }
</style>
